'use client'

import {
  GoogleTagManager as GTMScript,
  sendGTMEvent,
} from '@next/third-parties/google'
import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import { match } from 'path-to-regexp'
import { useEffect, useRef } from 'react'

import { publicRoutes } from '../auth.public-routes'
import { getAdviserUserType } from '../auth/adviser'
import { isCustomerAppRole } from '../auth/customer'
import { isImpersonatedUser } from '../auth/impersonated'
import { UserRole } from '../auth/types'

const redactedRoutes = [
  '/account/:r1/:r2',
  '/adviser/client/entity/:r1/account/:r2/:r3',
  '/adviser/client/entity/:r1/portfolio/:r2',
  '/adviser/client/individual/:r1/account/:r2/:r3',
  '/adviser/client/individual/:r1/portfolio/:r2',
  '/adviser/employees/:r/details',
  '/adviser/employees/:r/details/access-permissions',
  '/adviser/employees/:r/edit-mobile',
  '/adviser/employees/:r/update-email',
  '/adviser/entity/:r',
  '/adviser/firms/:r/details',
  '/adviser/firms/:r/employees',
  '/adviser/individual/:r',
  '/adviser/invite-employee/:r',
  '/portfolio/:r',
]

const matchRedactedRoutes = match(redactedRoutes, { end: false })

export function sanitizePath(path: string) {
  const matched = matchRedactedRoutes(path)
  const match = path.match(/\?([^?]*)$/)
  const search = match ? '?' + match[1] : ''

  if (matched && matched.params) {
    const sanitizedPath = Object.values(matched.params).reduce<string>(
      (sanitized, param) => {
        return sanitized.replace(param as string, '{redacted}')
      },
      path,
    )

    return sanitizedPath + search
  }

  return path
}

export function getUserType(
  roles?: UserRole[],
): 'corporate' | 'firm_user' | 'client' | 'impersonated' | undefined {
  if (isImpersonatedUser(roles)) {
    return 'impersonated'
  }

  if (isCustomerAppRole(roles)) {
    return 'client'
  }

  const adviserRoleType = getAdviserUserType(roles)

  if (adviserRoleType === 'firm') {
    return 'firm_user'
  } else if (adviserRoleType === 'corporate') {
    return 'corporate'
  }

  return undefined
}

function pageView({
  path,
  previousPath,
  userType,
}: {
  path: string
  previousPath: string | undefined
  userType: string | undefined
}) {
  const url = new URL(path, window.location.origin)

  const previousUrl =
    previousPath && new URL(previousPath, window.location.origin)

  return {
    event: 'pageView',
    pageView: path,
    pageUrl: decodeURI(url.toString()),
    pageReferrer: previousUrl
      ? decodeURI(previousUrl.toString())
      : document.referrer,
    userType,
  }
}

export function GoogleTagManager({ gtmId }: { gtmId: string }) {
  const pathname = usePathname()
  const session = useSession()
  const previousPath = useRef<string | undefined>(undefined)
  const isSessionReady = session?.status === 'authenticated'
  const roles = session?.data?.roles
  const userType = getUserType(roles)

  useEffect(() => {
    if (!isSessionReady) return

    if (!publicRoutes.includes(pathname)) {
      sendGTMEvent(
        pageView({
          path: sanitizePath(pathname),
          previousPath:
            previousPath.current && sanitizePath(previousPath.current),
          userType,
        }),
      )
    }

    previousPath.current = pathname
  }, [pathname, userType, isSessionReady])

  if (publicRoutes.includes(pathname)) {
    return null
  }

  return (
    <>
      <GTMScript gtmId={gtmId} />
    </>
  )
}
